import React, { FunctionComponent, useState } from 'react';
import withStyles from 'react-jss';
import { pathOr, replace, compose, path, filter } from 'ramda';
import { Container, Grid, Header } from 'semantic-ui-react';
import Capitalize from 'capitalize';

import Query from 'components/Query';
import Table from 'components/Table';
import { formatDate } from 'helpers/date';
import { ChartPlaceholder } from 'components/Placeholder';
import ResendTempPassword from 'scenes/UsersSummary/components/ResendTempPassword/ResendTempPassword';
import { ClassProp } from 'helpers/types';
import Search from 'components/Search/Search';

import GET_USERS_LIST from './ListUsers.queries';

import styles from './ListUsers.styles';

type UserAttributes = {
  sub: string;
  email: string;
  givenName: string;
  familyName: string;
  merchantId?: string;
  merchantName?: string;
  emailVerified?: 'true' | 'false';
  userType?: string;
  userRole?: string;
  identities?: string;
  userStatus: string;
};

type User = {
  username: string;
  userStatus: string;
  userCreateDate: Date;
  userLastModifiedDate: Date;
  attributes: UserAttributes;
};

type ListUsers = {
  users: User[];
  paginationToken?: boolean;
};

type QueryResponse = {
  data: { listUsers: ListUsers };
  loading: boolean;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
};

const formatUserStatus = compose(replace(/_/g, ' '), Capitalize.words);

const onClickHandler = (
  attributes: Pick<UserAttributes, 'email' | 'userStatus'>,
) => () => attributes;

const tableHeader = [
  {
    source: ['attributes', 'familyName'],
    sortable: ['attributes', 'familyName'],
    label: 'Last Name',
  },
  {
    source: ['attributes', 'givenName'],
    sortable: ['attributes', 'givenName'],
    label: 'First Name',
  },
  {
    source: ['attributes', 'email'],
    sortable: ['attributes', 'email'],
    label: 'Email',
  },
  {
    source: ['userCreateDate'],
    sortable: ['userCreateDate'],
    label: 'Create Date',
    transform: (date: string) => formatDate(date, 'MMM DD, YYYY'),
  },
  {
    source: ['attributes', 'merchantName'],
    sortable: ['attributes', 'merchantName'],
    label: 'Merchant',
    defaultValue: 'All',
  },
  {
    source: ['attributes', 'userType'],
    sortable: ['attributes', 'userType'],
    label: 'User Type',
  },
  {
    source: ['userStatus'],
    sortable: ['userStatus'],
    label: 'User Status',
    transform: formatUserStatus,
  },
  {
    label: 'Actions',
    source: ['attributes'],
    component: {
      Element: ResendTempPassword,
      props: {
        onClick: onClickHandler,
      },
    },
  },
];

const LOCALHOST_PROVIDER_NAME = 'localhost-Okta';

const usersFiltering = (user: User) => {
  try {
    const userIdentities = user.attributes.identities;

    if (userIdentities) {
      const userProviderName = path(
        [0, 'providerName'],
        JSON.parse(userIdentities),
      );

      return userProviderName !== LOCALHOST_PROVIDER_NAME;
    }

    return true;
  } catch (err) {
    return true;
  }
};

const getUsers = compose<any, User[], User[]>(
  filter(usersFiltering),
  pathOr([], ['listUsers', 'users']),
);

const renderTable = (
  listUsers: User[],
  page: number,
  loadMore: (direction: 'next' | 'previous') => void,
  paginationToken: string,
  classes: Styles,
) => {
  if (listUsers.length === 0) {
    return (
      <div className={classes.userNotFound}>
        <Header as="h2" className={classes.header} content="User Not Found!" />
      </div>
    );
  }

  return (
    <Table
      itemsKey="username"
      tableHeader={tableHeader}
      data={listUsers}
      sortDefaultColumn={{
        name: 'Last Name',
        sortable: ['attributes', 'familyName'],
      }}
      pagination={{
        page,
        loadMore,
        hasPrevious: page > 0,
        hasNext: !!paginationToken,
        pagesPerGroup: 3,
      }}
    />
  );
};

const renderListUsers = (
  classes: Styles,
  setPaginationTokens: Function,
  [page, setPage]: [number, React.Dispatch<React.SetStateAction<number>>],
) => (queryResponse: QueryResponse) => {
  const { data, loading } = queryResponse;

  const listUsers = getUsers(data).map((user) => {
    user.attributes.userStatus = user.userStatus;
    return user;
  });

  const paginationToken = path(['listUsers', 'paginationToken'], data || {});

  const loadMore = (direction: 'next' | 'previous') => {
    if (direction === 'next') {
      setPaginationTokens((tokens: string[]) => [...tokens, paginationToken]);
      setPage((page: number) => page + 1);
    } else {
      setPage((page: number) => page - 1);
    }
  };

  return (
    <div>
      {loading ? (
        <ChartPlaceholder className={classes.chartPlaceholder} />
      ) : (
        <Grid container>
          {renderTable(
            listUsers,
            page,
            loadMore,
            paginationToken as string,
            classes,
          )}
        </Grid>
      )}
    </div>
  );
};

const ListUsers: FunctionComponent<Props> = ({ classes }) => {
  const [paginationTokens, setPaginationTokens] = useState<string[]>([]);
  const [page, setPage] = useState<number>(0);
  const paginationToken = paginationTokens[page - 1];
  const [filter, setFilter] = useState<string>();

  const performSearch = (searchTerm: string) => {
    setFilter(`email  ^= \"${searchTerm}\"`);
  };

  const resetSearch = () => {
    setFilter('');
  };

  return (
    <Container className={classes.container}>
      <Grid container>
        <Search
          performSearch={performSearch}
          filter={filter ? filter.length > 0 : false}
          resetSearch={resetSearch}
        />
      </Grid>
      <Query
        enableProgressInfo
        query={GET_USERS_LIST}
        render={renderListUsers(classes, setPaginationTokens, [page, setPage])}
        variables={{
          params: {
            paginationToken,
            filter,
            userPoolId: process.env.COGNITO_USER_POOL_ID,
          },
        }}
      />
    </Container>
  );
};

export default withStyles(styles)(ListUsers);
